import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiDownload, FiFileText } from "react-icons/fi";
import { IoEyeOutline } from "react-icons/io5";
import { MdOutlineFileDownload } from "react-icons/md";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Button from "../../../components/common/Button/Button";
import Table from "../../../components/common/Table/Table";
import Badge from "../../../components/common/Badge/Badge";
import Modal from "../../../components/common/Modal/Modal";
import Notification from "../../../components/common/notification/notification";
import VisitaDetalleModal from "../../../components/layout/Visitas/VisitaDetalleModal";
import {
    BADGE_VALORACION_VISITA,
    descargarInformeVisitaWord,
    formatFechaVisita,
    mapVisitas,
} from "../../../components/layout/Visitas/visitaInforme";
import { getApiInstance } from "../../../utils/api";
import "./InformesVisitas.scss";

// Enlaza a la ficha del gestor abriendo directamente su pestaña de visitas
const fichaGestorVisitas = (codProveedor) =>
    `/gestores/${codProveedor}?tab=visitas`;

const columnasVisitas = [
    { key: "codVisita", label: "ID", visible: false },
    {
        key: "fechaVisita",
        label: "Fecha",
        visible: true,
        sortable: true,
        render: (row) => formatFechaVisita(row.fechaVisita),
    },
    {
        key: "titulo",
        label: "Informe",
        visible: true,
        sortable: true,
        render: (row) => (
            <div className="table-cell-content">
                <span className="table-cell-content__primary table-cell-content__primary--bold">
                    {row.titulo || "Informe de visita"}
                </span>
                {row.ubicacion && (
                    <span className="table-cell-content__secondary">
                        {row.ubicacion}
                    </span>
                )}
            </div>
        ),
    },
    {
        key: "proveedor",
        label: "Gestor",
        visible: true,
        sortable: true,
        render: (row) =>
            row.codProveedor ? (
                <Link to={fichaGestorVisitas(row.codProveedor)}>
                    {row.proveedor || `Gestor ${row.codProveedor}`}
                </Link>
            ) : (
                row.proveedor || "-"
            ),
    },
    {
        key: "valoracion",
        label: "Valoración",
        visible: true,
        sortable: true,
        render: (row) => {
            const badge = BADGE_VALORACION_VISITA[row.codValoracion];
            if (!badge) return "-";
            return (
                <Badge variant={badge.type} Icon={badge.Icon}>
                    {badge.label}
                </Badge>
            );
        },
    },
    {
        key: "tecnico",
        label: "Técnico",
        visible: true,
        sortable: true,
        visibleOnExpand: true,
    },
    {
        key: "tipoInstalacion",
        label: "Tipo de instalación",
        visible: true,
        sortable: true,
        visibleOnExpand: true,
    },
    {
        key: "documento",
        label: "Adjunto",
        visible: true,
        sortable: false,
        visibleOnExpand: true,
        type: "document",
        documentProp: "ficheroBase64",
    },
];

// El indice del labelMap es el codValoracion (1..3); el 0 no se usa en BD.
const filterFields = [
    { key: "proveedor", label: "Gestor", type: "multiselect" },
    {
        key: "codValoracion",
        label: "Valoración",
        type: "multiselect",
        labelMap: [
            "-",
            "Favorable",
            "Favorable con observaciones",
            "Desfavorable",
        ],
    },
    { key: "tecnico", label: "Técnico", type: "multiselect" },
    { key: "fechaVisita", label: "Fecha de visita", type: "daterange" },
    {
        key: "tipoInstalacion",
        label: "Tipo de instalación",
        type: "multiselect",
        advanced: true,
    },
];

// Campos internos/de auditoria del DTO que no deben ofrecerse como filtro avanzado
const excludeFilterFields = [
    "codVisita",
    "codProveedor",
    "codObra",
    "fichero",
    "documento",
    "ficheroBase64",
    "codUsuario",
    "titulo", 
    "codVisita", 
    "codProveedor", 
    "codObra", 
    "introduccion", 
    "objetivos", 
    "descripcion", 
    "conclusiones", 
    "descripcionInstalacion", 
    "observaciones", 
    "ficheroBase64", 
    "fichero", 
    "documento", 
    "codUsuario", 
    "desarrollo", 
    "recomendaciones",
    "ubicacion"
];

export default function InformesVisitas({ user }) {
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const notificationRef = useRef(null);
    const [visitas, setVisitas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [visitaSeleccionada, setVisitaSeleccionada] = useState(null);
    const [isVisitaModalOpen, setIsVisitaModalOpen] = useState(false);
    const [isModalExcelOpen, setIsModalExcelOpen] = useState(false);
    const [exportando, setExportando] = useState(null);
    const [errorExport, setErrorExport] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const fetchVisitas = async () => {
            setLoading(true);
            try {
                const api = getApiInstance();
                const response = await api.get(
                    "/api/ProveedoresVisitas/GetVisitas"
                );
                if (!isMounted) return;
                setVisitas(mapVisitas(response.data));
                setError(null);
            } catch (err) {
                if (isMounted) {
                    setError(
                        "No se han podido cargar los informes de visita. Vuelve a intentarlo más tarde."
                    );
                }
                console.error("Error fetching visitas:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchVisitas();

        return () => {
            isMounted = false;
        };
    }, []);

    const handleVerVisita = (visita) => {
        setVisitaSeleccionada(visita);
        setIsVisitaModalOpen(true);
    };

    const abrirModalExcel = () => {
        setErrorExport(null);
        setIsModalExcelOpen(true);
    };

    // Word de un informe suelto, el mismo que ofrece el modal de detalle
    const handleExportarWord = async (visita) => {
        try {
            await descargarInformeVisitaWord(visita);
        } catch (err) {
            console.error("Error exportando el informe de visita a Word:", err);
            notificationRef.current?.addNotification(
                "No se pudo generar el Word del informe de visita",
                "error"
            );
        }
    };

    // Se exporta lo que el usuario tiene delante: la tabla filtra en cliente, asi
    // que se le piden las filas visibles y se mandan sus codigos al back.
    const handleExportToExcel = async (tipo) => {
        if (exportando) return;

        const filasVisibles = tableRef.current?.getVisibleRows() ?? [];
        const codsVisitas = filasVisibles
            .map((fila) => fila.codVisita)
            .filter((cod) => cod != null);

        if (codsVisitas.length === 0) {
            setErrorExport(
                "No hay informes que exportar con los filtros aplicados."
            );
            return;
        }

        setExportando(tipo);
        setErrorExport(null);

        try {
            const api = getApiInstance();
            const response = await api.post(
                `/api/ProveedoresVisitas/CreateReportVisitas?completo=${
                    tipo === "completo"
                }`,
                codsVisitas,
                { responseType: "blob" }
            );

            const contentDisposition = response.headers["content-disposition"];
            let fileName = "Informes_Visitas.xlsx";
            if (contentDisposition) {
                // El header trae filename= y filename*=UTF-8''...; se prioriza este ultimo.
                const utf8Match = contentDisposition.match(
                    /filename\*=UTF-8''([^;]+)/i
                );
                if (utf8Match && utf8Match[1]) {
                    fileName = decodeURIComponent(utf8Match[1]);
                } else {
                    const fileNameMatch = contentDisposition.match(
                        /filename="?([^";]+)"?/i
                    );
                    if (fileNameMatch && fileNameMatch[1]) {
                        fileName = fileNameMatch[1];
                    }
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setIsModalExcelOpen(false);
        } catch (err) {
            console.error("Error exportando informes de visita:", err);
            setErrorExport(
                "No se pudo generar el Excel. Vuelve a intentarlo más tarde."
            );
        } finally {
            setExportando(null);
        }
    };

    return (
        <PageLayout
            title="Informes de visitas"
            subtitle="Informes de todas las visitas realizadas a los gestores"
            actions={[
                <Button
                    key="exportar"
                    variant="filled"
                    startIcon={FiDownload({})}
                    endIcon={undefined}
                    onClick={abrirModalExcel}
                    disabled={Boolean(exportando)}
                >
                    {exportando ? "Generando..." : "Exportar a Excel"}
                </Button>,
            ]}
        >
            <div className="informes-visitas">
                {error ? (
                    <div className="informes-visitas__error">{error}</div>
                ) : (
                    <Table
                        ref={tableRef}
                        columns={columnasVisitas}
                        dataTable={visitas}
                        keyProp="codVisita"
                        sortProp="fechaVisita"
                        searchTerms={[
                            "titulo",
                            "proveedor",
                            "tecnico",
                            "ubicacion",
                        ]}
                        filterFields={filterFields}
                        excludeFields={excludeFilterFields}
                        noResultsMessage="No hay informes de visita registrados"
                        rowsPerPage={10}
                        showCheckboxes={false}
                        hideAddButton={true}
                        expandable={true}
                        expandableBreakpoint={600}
                        elementName="Informe de visita"
                        stateProp=""
                        loading={loading}
                        // Sin estos dos la tabla no pinta la columna de acciones
                        userCodAcceso={Number(user?.codAcceso)}
                        userCodPerfil={Number(user?.role)}
                        actions={[
                            {
                                label: "Ver informe",
                                icon: <IoEyeOutline size={16} />,
                                action: (row) => handleVerVisita(row),
                            },
                            {
                                label: "Exportar a Word",
                                icon: <MdOutlineFileDownload size={16} />,
                                action: (row) => handleExportarWord(row),
                            },
                            {
                                label: "Ficha del gestor",
                                icon: <FiFileText size={16} />,
                                action: (row) =>
                                    navigate(
                                        fichaGestorVisitas(row.codProveedor)
                                    ),
                            },
                        ]}
                    />
                )}
            </div>

            <VisitaDetalleModal
                isOpen={isVisitaModalOpen}
                onClose={() => setIsVisitaModalOpen(false)}
                visita={visitaSeleccionada}
            />

            <Modal
                isOpen={isModalExcelOpen}
                onClose={() => setIsModalExcelOpen(false)}
                title="Exportar informes de visita a Excel"
                actions={[
                    <Button
                        key="export-resumen"
                        className="export-excel__btn export-excel__btn--primary"
                        onClick={() => handleExportToExcel("resumen")}
                        startIcon={<MdOutlineFileDownload />}
                        disabled={Boolean(exportando)}
                    >
                        {exportando === "resumen"
                            ? "Generando..."
                            : "Resumen"}
                    </Button>,
                    <Button
                        key="export-completo"
                        className="export-excel__btn export-excel__btn--secondary"
                        onClick={() => handleExportToExcel("completo")}
                        startIcon={<MdOutlineFileDownload />}
                        disabled={Boolean(exportando)}
                    >
                        {exportando === "completo"
                            ? "Generando..."
                            : "Completo"}
                    </Button>,
                ]}
            >
                <div className="informes-visitas__export">
                    <p>
                        Se exportarán los informes que se ven en la tabla con los
                        filtros aplicados.
                    </p>
                    <ul>
                        <li>
                            <strong>Resumen</strong>: una hoja con una fila por
                            visita (fecha, gestor, ubicación, tipo de
                            instalación, técnico, valoración y adjunto).
                        </li>
                        <li>
                            <strong>Completo</strong>: además, una segunda hoja
                            con los siete bloques de texto de cada informe.
                        </li>
                    </ul>
                    {errorExport && (
                        <div className="informes-visitas__error">
                            {errorExport}
                        </div>
                    )}
                </div>
            </Modal>

            <Notification ref={notificationRef} />
        </PageLayout>
    );
}
